/**
 * Godot's transform flushes: the walks of the SceneTree's transform-change list that send
 * `NOTIFICATION_TRANSFORM_CHANGED`, and how many of them run before the `ref:godot` harness
 * captures a scene. Measured on Godot 4.6.3 with the harness's `--fixed-fps 1000`.
 */
import { physicsTicksThrough } from './physicsTicks.js';
/**
 * The frame the `ref:godot` harness captures a scene at when its transforms never hold still, and
 * its `--fixed-fps`. `run.test.mjs` checks both against the harness's own constants.
 *
 * @canonical The process frame the ref:godot harness captures a never-settling scene at
 */
export const HARNESS_CAPTURE = { frame: 923, fixedFps: 1000 };
/**
 * The transform flushes Godot runs from the moment a scene enters the tree to the end of `at`:
 * three in each process frame (`scene_tree.cpp:706,712,721`) and two in each physics tick
 * (`:631`, `:652`) that `clock` paces.
 */
export function transformFlushesThrough({ frame, fixedFps }, clock) {
    return 3 * (frame + 1) + 2 * physicsTicksThrough(frame, fixedFps, clock);
}
/**
 * Replays `flushes` walks of the transform-change list and calls `notify` for each entry a walk
 * reaches. `entered` holds the entries in the order they enter the tree. An add puts an entry at
 * the front (`self_list.h:43-58`), so the list starts in reverse order. A walk takes the entries
 * the list held when it began (`scene_tree.cpp:193-204`): one that `notify` queues waits for the
 * next walk, and one still on the list keeps its place. The replay ends early on an empty list.
 */
export function replayTransformFlushes(entered, flushes, notify) {
    const onList = new Set(entered);
    let list = [...entered].reverse();
    let queuedNewestLast = [];
    const queue = (entry) => {
        if (onList.has(entry))
            return;
        onList.add(entry);
        queuedNewestLast.push(entry);
    };
    for (let flush = 0; flush < flushes && list.length > 0; flush++) {
        for (const entry of list) {
            onList.delete(entry);
            notify(entry, queue);
        }
        list = queuedNewestLast.reverse();
        queuedNewestLast = [];
    }
}
//# sourceMappingURL=transformFlushes.js.map